// src/hooks/useNotifications.js

import { useState, useEffect, useRef, useCallback } from "react";
import { db } from "../firebase";
import { collection, query, where, getDocs, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { dismissNotificationForUser } from "../services/notificationService";
import { retryOnPermissionDenied } from "../utils/retryOnPermissionDenied";

const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, startTime, duration) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type            = "sine";
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = ctx.currentTime;
    playTone(880,  now,        0.15);
    playTone(1100, now + 0.18, 0.20);
  } catch (e) {
    console.warn("[Notifications] Sound error:", e);
  }
};

const POLL_INTERVAL_MS = 60_000;
const STORAGE_KEY      = "zeno_notif_last_count";

export function useNotifications() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const uid       = user.uid || user.id || "";
  const companyId = user.company_id || "";
  const role      = (user.role || "").toLowerCase().trim();

  const [notifications, setNotifications] = useState([]);
  const [totalCount,    setTotalCount]    = useState(0);
  const [loading,       setLoading]       = useState(false);

  const lastCountRef = useRef(Number(localStorage.getItem(STORAGE_KEY) || 0));

  const fetchNotifications = useCallback(async () => {
    if (!uid || !companyId) return;
    setLoading(true);

    try {
      const actionItems = [];
      const infoItems   = [];

      // ── 1. PENDING ACCEPTANCE (asset transfer) ────────
      // Wrapped in retryOnPermissionDenied — first company_id-scoped
      // query in this function, so retrying it covers the post-login
      // claims race for every query below too (12 Aug 2026 audit).
      const pendingAcceptSnap = await retryOnPermissionDenied(() => getDocs(query(
        collection(db, "asset_transactions"),
        where("company_id",     "==", companyId),
        where("to_id",          "==", uid),
        where("workflow_state", "==", "PENDING_ACCEPTANCE")
      )));
      pendingAcceptSnap.forEach(d => {
        actionItems.push({
          id: d.id, type: "pending_acceptance",
          notification_class: "action_required",
          title: "🔧 Tool Transfer — Accept Required",
          body:  `${d.data().asset_name || "Asset"} from ${d.data().from_holder_name || "Store"}`,
          route: "/assets/pending", priority: "high",
        });
      });

      // ── 2. RETURN PENDING (store/SM) ──────────────────
      if (["sm", "store", "manager", "owner"].includes(role)) {
        const returnSnap = await getDocs(query(
          collection(db, "asset_transactions"),
          where("company_id",     "==", companyId),
          where("workflow_state", "==", "RETURN_PENDING")
        ));
        returnSnap.forEach(d => {
          actionItems.push({
            id: d.id, type: "return_pending",
            notification_class: "action_required",
            title: "↩️ Asset Return — Receive Required",
            body:  `${d.data().asset_name || "Asset"} from ${d.data().from_holder_name || "Staff"}`,
            route: "/assets/receive-returns", priority: "medium",
          });
        });
      }

      // ── 3. PENDING SEND ───────────────────────────────
      const pendingSendSnap = await getDocs(query(
        collection(db, "asset_transactions"),
        where("company_id",     "==", companyId),
        where("from_id",        "==", uid),
        where("workflow_state", "==", "PENDING_ACCEPTANCE")
      ));
      pendingSendSnap.forEach(d => {
        actionItems.push({
          id: d.id, type: "pending_send",
          notification_class: "action_required",
          title: "📤 Transfer Pending — Hand Over",
          body:  `${d.data().asset_name || "Asset"} → ${d.data().to_holder_name || "Receiver"}`,
          route: "/assets/pending", priority: "medium",
        });
      });

      // ── 4. MRF PENDING ────────────────────────────────
      if (["sm", "store", "manager", "owner"].includes(role)) {
        const mrfSnap = await getDocs(query(
          collection(db, "material_requests"),
          where("company_id", "==", companyId),
          where("status",     "==", "pending")
        ));
        mrfSnap.forEach(d => {
          const isToolReq = d.data().request_type === "tool";
          actionItems.push({
            id: d.id, type: "mrf_pending",
            notification_class: "action_required",
            title: isToolReq ? "🔧 Tool Request — Approval Needed" : "📋 MRF — Approval Needed",
            body:  `From ${d.data().requested_by_name || "Staff"}${d.data().site_name ? ` — ${d.data().site_name}` : ""}`,
            route: "/store-mrf", priority: isToolReq ? "high" : "medium",
          });
        });
      }

      // ── 5. EXPENSE PENDING ────────────────────────────
      if (["sm", "store", "manager", "owner"].includes(role)) {
        const expSnap = await getDocs(query(
          collection(db, "site_purchase_requests"),
          where("company_id", "==", companyId),
          where("status",     "==", "pending"),
          where("type",       "==", "expense")
        ));
        expSnap.forEach(d => {
          actionItems.push({
            id: d.id, type: "expense_pending",
            notification_class: "action_required",
            title: "💰 Expense — Approval Needed",
            body:  `₹${d.data().amount || 0} from ${d.data().requested_by_name || "Staff"}`,
            route: "/expense-approval", priority: "medium",
          });
        });
      }

      // ── 6. SERVICE TASKS — assigned to me (technician) ─
      if (["technician"].includes(role) && uid) {
        const svcSnap = await getDocs(query(
          collection(db, "service_tasks"),
          where("company_id", "==", companyId),
          where("status",     "in", ["assigned", "open"])
        ));
        svcSnap.forEach(d => {
          const t = d.data();
          const isAssigned =
            t.assigned_to === uid ||
            (t.assigned_techs || []).some(tech => tech.id === uid);
          if (!isAssigned) return;
          actionItems.push({
            id: d.id, type: "service_assigned",
            notification_class: "action_required",
            title: `🔧 Task Assigned — ${t.client_name || "Client"}`,
            body:  `${t.complaint || "Service task"} at ${t.site_name || ""}`,
            route: `/tasks/${d.id}`, priority: "high",
          });
        });
      }

      // ── 7. SERVICE PENDING APPROVAL (SM/Manager) ─────
      if (["sm", "manager", "owner"].includes(role)) {
        const svcApprSnap = await getDocs(query(
          collection(db, "service_tasks"),
          where("company_id", "==", companyId),
          where("status",     "==", "completed_pending_approval")
        ));
        svcApprSnap.forEach(d => {
          const t = d.data();
          actionItems.push({
            id: d.id, type: "service_approval_pending",
            notification_class: "action_required",
            title: `✅ Service Completed — Approval Needed`,
            body:  `${t.client_name || ""} — ${t.site_name || ""}`,
            route: `/services/${d.id}`, priority: "high",
          });
        });
      }

      // ── 8. PROJECT MRF PENDING ────────────────────────
      if (["sm", "manager", "owner"].includes(role)) {
        const pmrfSnap = await getDocs(query(
          collection(db, "project_mrfs"),
          where("company_id", "==", companyId),
          where("status",     "==", "pending")
        ));
        pmrfSnap.forEach(d => {
          const m = d.data();
          actionItems.push({
            id: d.id, type: "project_mrf_pending",
            notification_class: "action_required",
            title: `📋 Project MRF — Approval Needed`,
            body:  `${m.project_name || ""} — ${m.stage_name || ""} — ${m.requested_by_name || ""}`,
            route: `/projects/${m.project_id}/materials`, priority: "medium",
          });
        });
      }

      // ── 9. STOCK ADJUSTMENT PENDING ───────────────────
      if (["sm", "manager", "owner"].includes(role)) {
        const adjSnap = await getDocs(query(
          collection(db, "stock_adjustments"),
          where("company_id", "==", companyId),
          where("status",     "==", "pending")
        ));
        adjSnap.forEach(d => {
          const a = d.data();
          actionItems.push({
            id: d.id, type: "stock_adjustment_pending",
            notification_class: "action_required",
            title: `📊 Stock Adjustment — Approval Needed`,
            body:  `${a.adjustment_no || ""} — ${a.item_name || ""}`,
            route: "/stock-adjustment-approval", priority: "medium",
          });
        });
      }

      // ── 10. FIRESTORE NOTIFICATIONS — action_required ─
      const actionNotifSnap = await getDocs(query(
        collection(db, "notifications"),
        where("company_id", "==", companyId),
        where("notification_class", "==", "action_required"),
        where("resolved",   "==", false)
      ));
      actionNotifSnap.forEach(d => {
        const n = d.data();
        if (n.for_roles?.length > 0 && !n.for_roles.includes(role)) return;
        if (n.for_uid && n.for_uid !== uid) return;
        if ((n.dismissed_by || []).includes(uid)) return; // already dismissed by this user
        actionItems.push({
          id: d.id, type: n.type || "notification",
          notification_class: "action_required",
          title:    n.title    || "Notification",
          body:     n.body     || "",
          route:    n.route    || "",
          priority: n.priority || "medium",
          reference_id: n.reference_id || "",
          company_id:   n.company_id   || "",
          metadata:     n.metadata     || {},
          is_read:  false,
          _fromFirestore: true,
          _autoRead: false, // action_required kabhi auto-read nahi
        });
      });

      // ── 10b. FIRESTORE NOTIFICATIONS — informational ──
      const infoNotifSnap = await getDocs(query(
        collection(db, "notifications"),
        where("company_id", "==", companyId),
        where("notification_class", "==", "informational"),
        where("resolved",   "==", false)
      ));
      infoNotifSnap.forEach(d => {
        const n = d.data();
        if (n.for_roles?.length > 0 && !n.for_roles.includes(role)) return;
        if (n.for_uid && n.for_uid !== uid) return;
        if ((n.dismissed_by || []).includes(uid)) return; // already dismissed by this user
        infoItems.push({
          id: d.id, type: n.type || "notification",
          notification_class: "informational",
          title:    n.title    || "Notification",
          body:     n.body     || "",
          route:    n.route    || "",
          priority: n.priority || "medium",
          reference_id: n.reference_id || "",
          company_id:   n.company_id   || "",
          metadata:     n.metadata     || {},
          is_read:  false,
          _fromFirestore: true,
          _autoRead: true, // informational bell open hone pe auto-read
        });
      });

      // ── Sort ──────────────────────────────────────────
      const sortByPriority = (a, b) => {
        const order = { high: 0, medium: 1, low: 2 };
        return (order[a.priority] || 1) - (order[b.priority] || 1);
      };
      actionItems.sort(sortByPriority);
      infoItems.sort(sortByPriority);

      // Action required first, then informational
      const allItems = [...actionItems, ...infoItems];

      const newCount = allItems.length;
      if (newCount > lastCountRef.current) playNotificationSound();
      lastCountRef.current = newCount;
      localStorage.setItem(STORAGE_KEY, String(newCount));

      setNotifications(allItems);
      setTotalCount(newCount);

    } catch (err) {
      console.error("[Notifications] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [uid, companyId, role]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  useEffect(() => {
    const timer = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchNotifications]);

  const refresh     = useCallback(() => fetchNotifications(), [fetchNotifications]);
  const markAllSeen = useCallback(() => {
    lastCountRef.current = totalCount;
    localStorage.setItem(STORAGE_KEY, String(totalCount));
  }, [totalCount]);

  // FIXED — this previously dismissed every informational notification the
  // instant the bell was opened, regardless of whether the user actually
  // read it. For a multi-role notification (for_roles: ["owner","manager"]),
  // that meant it could vanish for a recipient before they'd seen it at
  // all — simply opening the bell for an unrelated reason silently
  // consumed it. Dismissal now only happens on actual click
  // (NotificationBell.jsx's handleNotifClick), same as action_required —
  // this function is kept as a no-op stub so existing callers don't break,
  // but it no longer dismisses anything.
  const markInfoRead = useCallback(() => {
    // Intentionally does nothing now — see comment above.
  }, []);

  return { notifications, totalCount, loading, refresh, markAllSeen, markInfoRead };
}